<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { toast } from '~/utils/sonner'
import { verifyMacOSReleaseFile } from '~/utils/macosUpdate'
import { readMacOSReleaseDrop, readMacOSReleaseFiles } from '~/utils/macosReleaseInput'
import type { MacOSReleaseMetadata, MacOSReleaseTarget, MacOSUpdateState, MacOSVersion } from '~/utils/macosUpdate'

const state = ref<MacOSUpdateState>({ channels: { stable: null, beta: null }, downloads: { tutorial: null }, versions: [] })
const metadata = ref<MacOSReleaseMetadata | null>(null)
const file = ref<File | null>(null)
const fileHash = ref('')
const stagedKey = ref<string | null>(null)
const hashing = ref(false)
const busy = ref(false)
const progress = ref<number | null>(null)
const error = ref('')
const status = ref('')
const editing = ref<MacOSVersion | null>(null)
const minBuild = ref(0)
const notes = ref('')
const notesEn = ref('')
const fileInput = ref<HTMLInputElement | null>(null)
const folderInput = ref<HTMLInputElement | null>(null)
const dragDepth = ref(0)
const locked = computed(() => busy.value || hashing.value)
const targets: MacOSReleaseTarget[] = ['stable', 'beta', 'tutorial']
const targetLabel = (target: MacOSReleaseTarget) => target === 'tutorial' ? '官网下载' : target
const current = (target: MacOSReleaseTarget) => target === 'tutorial' ? state.value.downloads.tutorial : state.value.channels[target]
const canSave = computed(() => metadata.value && !hashing.value && !busy.value && (editing.value || (file.value && fileHash.value)))
const errorMessage = (value: any) => value?.data?.message || value?.message || '操作失败'

async function refresh() {
  if (locked.value) return
  busy.value = true
  error.value = ''
  try { state.value = await useAdmin.getMacOSUpdateVersions() }
  catch (cause) { error.value = errorMessage(cause) }
  finally { busy.value = false }
}

async function importRelease(files: Promise<File[]>) {
  if (locked.value || editing.value) return
  metadata.value = null; file.value = null; fileHash.value = ''; stagedKey.value = null
  error.value = ''
  hashing.value = true
  try {
    const release = await readMacOSReleaseFiles(await files)
    metadata.value = release.metadata
    file.value = release.file
    fileHash.value = release.hash
  }
  catch (cause) { error.value = errorMessage(cause) }
  finally { hashing.value = false }
  if (metadata.value) await save()
}

function selectRelease(event: Event) {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files ?? [])
  input.value = ''
  if (files.length && !locked.value) void importRelease(Promise.resolve(files))
}

function dropRelease(event: DragEvent) {
  dragDepth.value = 0
  if (locked.value || editing.value || !event.dataTransfer) return
  void importRelease(readMacOSReleaseDrop(event.dataTransfer))
}

function dragOver(event: DragEvent) {
  if (event.dataTransfer) event.dataTransfer.dropEffect = locked.value ? 'none' : 'copy'
}

function uploadPackage(url: string, contentType: string, body: File) {
  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', url)
    request.timeout = 15 * 60 * 1000
    request.setRequestHeader('Content-Type', contentType)
    request.upload.onprogress = event => { progress.value = event.lengthComputable ? Math.round(event.loaded / event.total * 100) : null }
    request.onload = () => request.status >= 200 && request.status < 300 ? resolve() : reject(new Error(`上传失败：HTTP ${request.status}`))
    request.onerror = () => reject(new Error('上传网络错误，请重试'))
    request.ontimeout = () => reject(new Error('上传超时，请重试'))
    request.send(body)
  })
}

async function save() {
  if (!canSave.value || !metadata.value) return
  error.value = ''
  const release = metadata.value
  busy.value = true
  try {
    if (!editing.value) {
      if (!file.value) throw new Error('请选择 DMG')
      verifyMacOSReleaseFile(release, file.value, fileHash.value)
    }
    if (!Number.isInteger(minBuild.value) || minBuild.value < 0 || minBuild.value > release.versionCode) throw new Error('最低 build 必须在 0 到当前 build 之间')
    if (editing.value && await useDialog().confirm({ title: '保存版本信息',
      body: `${release.versionName} · build ${release.versionCode}\n登记后不会自动推送，需要单独选择发布通道。`,
      confirmButtonText: '确认', cancelButtonText: '取消' }) !== 'confirm') return
    let key = editing.value?.extra.r2_key || stagedKey.value
    if (!key) {
      const upload = await useAdmin.createMacOSUpdateUploadUrl(release)
      status.value = '正在上传 DMG'
      progress.value = 0
      await uploadPackage(upload.uploadUrl, upload.contentType, file.value!)
      key = upload.key
      // Keep a completed upload across a failed registration response so a
      // retry uses the idempotent registration endpoint, not a new upload.
      stagedKey.value = key
    }
    if (!key) throw new Error('发布记录缺少安装包路径')
    status.value = '正在登记版本'
    const result = await useAdmin.registerMacOSUpdateVersion({ ...release, key,
      fileName: file.value?.name || editing.value!.extra.original_filename,
      minVersionCode: minBuild.value, releaseNotes: notes.value, releaseNotesEn: notesEn.value })
    state.value = result.state
    resetDraft()
    toast({ title: 'macOS 版本已登记' })
  }
  catch (cause) { error.value = errorMessage(cause) }
  finally { busy.value = false; progress.value = null; status.value = '' }
}

async function promote(target: MacOSReleaseTarget, version: MacOSVersion | null) {
  if (locked.value) return
  busy.value = true
  error.value = ''
  try {
    if (await useDialog().confirm({ title: `${version ? '发布到' : '撤下'} ${targetLabel(target)}`,
      body: version ? `将指向 ${version.version} (build ${version.versionCode})。` : '通道将不再提供版本，已登记的安装包会保留。',
      confirmButtonText: '确认', cancelButtonText: '取消' }) !== 'confirm') return
    const result = version ? await useAdmin.promoteMacOSUpdateVersion({ target, versionCode: version.versionCode })
      : await useAdmin.unpromoteMacOSUpdateVersion({ target })
    state.value = result.state
  }
  catch (cause) { error.value = errorMessage(cause) }
  finally { busy.value = false }
}

function resetDraft() {
  editing.value = null; metadata.value = null; file.value = null; fileHash.value = ''
  stagedKey.value = null
  notes.value = ''; notesEn.value = ''; minBuild.value = 0
}

function edit(version: MacOSVersion) {
  if (locked.value) return
  resetDraft()
  editing.value = version
  metadata.value = { ...version, versionName: version.version }
  notes.value = version.releaseNotes; notesEn.value = version.releaseNotesEn
  minBuild.value = version.minVersionCode
}

onMounted(refresh)
</script>

<template lang="pug">
div(class="mx-auto w-full max-w-7xl space-y-8 px-2 py-6")
  header(class="flex items-start justify-between gap-4 border-b pb-5")
    div(class="space-y-1")
      h1(class="text-2xl font-semibold tracking-tight") PandaFan macOS 更新
      p(class="text-sm text-muted-foreground") 原生 macOS 独立分发，不影响 Electron 桌面版。仅上传已签名、公证的 Universal DMG。
    Button(variant="outline" :disabled="locked" @click="refresh") 刷新
  p(v-if="error" role="alert" class="rounded-md border border-destructive/40 p-4 text-sm text-destructive") {{ error }}
  section(class="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]")
    form(class="space-y-6" @submit.prevent="save")
      h2(class="text-lg font-medium") {{ editing ? '编辑发布信息' : '上传新版本' }}
      p(class="text-sm text-muted-foreground") 在 BaoLianDeng 目录运行 make build-macos，将生成的两个文件或发布文件夹拖入下方。
      fieldset(:disabled="busy || hashing" class="space-y-5")
        template(v-if="!editing")
          div(class="rounded-lg border-2 border-dashed p-8 text-center transition-colors" :class="dragDepth > 0 && !locked ? 'border-primary bg-primary/5' : 'border-muted-foreground/30'" :aria-busy="locked" @dragenter.prevent="dragDepth++" @dragleave.prevent="dragDepth = Math.max(0, dragDepth - 1)" @dragover.prevent="dragOver" @drop.prevent="dropRelease")
            p(class="font-medium") 拖入 release.json 和 DMG，或包含它们的发布文件夹
            p(class="mt-2 text-sm text-muted-foreground") 校验通过后自动上传并登记。发布通道需要单独选择。
            div(class="mt-5 flex flex-wrap justify-center gap-3")
              Button(type="button" variant="outline" :disabled="locked" @click="fileInput?.click()") 选择两个文件
              Button(type="button" variant="outline" :disabled="locked" @click="folderInput?.click()") 选择文件夹
            input(ref="fileInput" class="hidden" type="file" accept=".json,.dmg" multiple @change="selectRelease")
            input(ref="folderInput" class="hidden" type="file" webkitdirectory multiple @change="selectRelease")
        div(v-if="metadata" class="space-y-1 border-l-2 border-primary pl-4 text-sm")
          p(class="font-medium") {{ metadata.versionName }} · build {{ metadata.versionCode }} · macOS {{ metadata.minimumSystemVersion }}+
          p {{ (metadata.size / 1024 / 1024).toFixed(1) }} MB · Universal
          p(class="break-all text-xs text-muted-foreground") SHA-256: {{ metadata.sha256 }}
        label(class="grid max-w-xs gap-2 text-sm")
          span 最低支持 build
          Input(v-model.number="minBuild" type="number" min="0" :max="metadata?.versionCode")
          span(class="text-xs text-muted-foreground") 0 为普通更新；低于此 build 的用户会收到不可跳过的关键更新提示，但不会静默安装。
        div(class="grid gap-5 md:grid-cols-2")
          label(class="grid gap-2 text-sm")
            span 更新说明（中文）
            Textarea(v-model="notes" rows="6")
          label(class="grid gap-2 text-sm")
            span Release Notes（English）
            Textarea(v-model="notesEn" rows="6")
      div(class="flex items-center gap-3")
        Button(v-if="editing || metadata" type="submit" :disabled="!canSave") {{ editing ? '保存修改' : '重试上传并登记' }}
        Button(v-if="editing" type="button" variant="outline" :disabled="busy" @click="resetDraft") 取消编辑
        span(role="status" aria-live="polite" class="text-sm text-muted-foreground") {{ hashing ? '正在读取并校验发布文件…' : status }} {{ progress !== null ? `${progress}%` : '' }}
    aside(class="space-y-5 border-t pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0")
      h2(class="text-lg font-medium") 当前分发
      div(v-for="target in targets" :key="target" class="space-y-2 border-b pb-4")
        h3(class="text-sm font-medium") {{ targetLabel(target) }}
        p(class="text-sm text-muted-foreground") {{ current(target) ? `${current(target)?.version} · build ${current(target)?.versionCode}` : '未设置' }}
        Button(v-if="current(target)" variant="ghost" size="sm" :disabled="locked" @click="promote(target, null)") 撤下
      p(class="text-xs leading-relaxed text-muted-foreground") 上传只登记版本；stable、beta 和官网下载分别发布。撤下不会删除文件，也不会让已安装用户降级。
  section(class="space-y-4 border-t pt-6")
    h2(class="text-lg font-medium") 已登记版本
    p(v-if="!state.versions.length" class="text-sm text-muted-foreground") 尚无原生 macOS 发布包。先上传 DMG，再选择分发通道。
    div(class="overflow-x-auto")
      table(v-if="state.versions.length" class="w-full text-left text-sm")
        thead(class="border-b text-muted-foreground")
          tr
            th(class="py-3 pr-4 font-medium") 版本
            th(class="py-3 pr-4 font-medium") 系统 / 大小
            th(class="py-3 pr-4 font-medium") 登记时间
            th(class="py-3 font-medium") 分发
        tbody
          tr(v-for="version in state.versions" :key="version.versionCode" class="border-b")
            td(class="py-4 pr-4")
              a(:href="version.downloadUrl" class="font-medium underline" target="_blank" rel="noreferrer") {{ version.version }} · {{ version.versionCode }}
              p(class="text-xs text-muted-foreground") 最低 build {{ version.minVersionCode }}
            td(class="py-4 pr-4 whitespace-nowrap") {{ version.minimumSystemVersion }}+ · {{ (version.size / 1024 / 1024).toFixed(1) }} MB
            td(class="py-4 pr-4 whitespace-nowrap") {{ version.createdAt.slice(0, 16).replace('T', ' ') }} UTC
            td(class="py-4")
              div(class="flex flex-wrap gap-2")
                Button(v-for="target in targets" :key="target" size="sm" :variant="current(target)?.versionCode === version.versionCode ? 'secondary' : 'outline'" :disabled="locked || current(target)?.versionCode === version.versionCode" @click="promote(target, version)") {{ targetLabel(target) }}{{ current(target)?.versionCode === version.versionCode ? ' ✓' : '' }}
                Button(size="sm" variant="ghost" :disabled="locked" @click="edit(version)") 编辑
</template>
